import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileXls, Timer, Clock, FunnelSimple, Truck, UserList, IdentificationBadge, MagnifyingGlass, ArrowsClockwise, X, ChartLineUp } from '@phosphor-icons/react';
import { EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';
import { DaywiseProductivity } from '../components/reports/DaywiseProductivity';

function FilterActionButtons({ onSearch, onReset, loading, hasActiveFilters }) {
  return (
    <div className="flex items-center gap-2 mt-4 flex-wrap">
      <Button data-testid="report-search-btn" onClick={onSearch} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 px-5">
        <MagnifyingGlass size={16} weight="bold" className="mr-1.5" />
        {loading ? 'Searching...' : 'Search'}
      </Button>
      {hasActiveFilters && (
        <Button data-testid="report-reset-btn" onClick={onReset} variant="outline" className="h-10 px-4 border-red-300 text-red-600 hover:bg-red-50 font-bold">
          <X size={16} weight="bold" className="mr-1.5" />
          Clear Filters
        </Button>
      )}
      <Button data-testid="report-refresh-btn" onClick={onSearch} variant="outline" className="h-10 px-4 font-bold">
        <ArrowsClockwise size={16} weight="bold" className="mr-1.5" />
        Refresh
      </Button>
    </div>
  );
}

function getInstallStatusLabel(status) {
  if (status === 'installation_completed') return 'Done';
  if (status === 'pending_for_installation') return 'Pending';
  return '--';
}

function getInstallStatusExportLabel(status) {
  if (status === 'installation_completed') return 'Completed';
  if (status === 'pending_for_installation') return 'Pending';
  return '';
}

function getInstallStatusBadgeClass(label) {
  if (label === 'Done') return 'bg-teal-100 text-teal-700';
  if (label === 'Pending') return 'bg-indigo-100 text-indigo-700';
  return 'bg-zinc-100 text-zinc-500';
}


function formatDuration(minutes) {
  if (!minutes) return '--';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
}

function formatDateTime(isoString) {
  if (!isoString) return '--';
  const d = new Date(isoString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/* ========== JO Export Tab (existing) ========== */
function JOExportTab() {
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [reportData, setReportData] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const fetchReportPreview = useCallback(async (statusOverride) => {
    setFetchingData(true);
    setFetchError(false);
    try {
      const params = new URLSearchParams();
      const s = statusOverride !== undefined ? statusOverride : filterStatus;
      if (s !== 'all') params.append('status', s);
      const { data } = await api.get(`/api/export/job-orders?${params}`);
      setReportData(data);
    } catch (err) {
      if (err?.isAuthError) return;
      setFetchError(true);
    } finally {
      setFetchingData(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchReportPreview(); }, []);

  const handleSearch = () => { fetchReportPreview(); };
  const handleReset = () => { setFilterStatus('all'); fetchReportPreview('all'); };
  const hasActiveFilters = filterStatus !== 'all';

  const totalWindows = reportData.reduce((sum, jo) => sum + (jo.no_of_windows || 0), 0);
  const totalSqft = reportData.reduce((sum, jo) => sum + (jo.total_sqft || 0), 0);

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const headers = [
        'Job Order No', 'JO Date', 'Customer Name', 'Customer Mobile', 'Work Location',
        'Quotation No', 'Executive Name', 'Total Sqft', 'No of Windows', 'Delivery Date',
        'Profile Type', 'Glass Type', 'Priority', 'Status', 'Created By', 'Updated By', 'Last Updated'
      ];
      const rows = reportData.map(jo => [
        jo.jo_number, jo.jo_date, jo.customer_name, jo.customer_mobile, jo.work_location,
        jo.quotation_no, jo.executive_name, jo.total_sqft, jo.no_of_windows, jo.delivery_date,
        jo.profile_type, jo.glass_type, jo.priority || 0, jo.status, jo.created_by, jo.updated_by || '', jo.updated_at || ''
      ]);
      let csv = headers.join(',') + '\n';
      rows.forEach(row => { csv += row.map(cell => `"${cell}"`).join(',') + '\n'; });
      csv += `\n"TOTALS","","","","","","","${totalSqft}","${totalWindows}","","","","","","","",""`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job_orders_${filterStatus}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success(`Exported ${reportData.length} job orders successfully`);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error state */}
      {fetchError && !fetchingData && (
        <Card className="border-red-200 rounded-sm shadow-sm bg-red-50">
          <CardContent className="p-6 text-center">
            <p className="text-red-700 font-bold mb-3">Failed to load report data</p>
            <p className="text-sm text-red-600 mb-4">There was an error fetching the reports. Please try again.</p>
            <Button data-testid="report-retry-button" onClick={() => fetchReportPreview()} className="bg-red-600 hover:bg-red-700 text-white font-bold h-10 px-6">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {fetchingData && (
        <Card className="border-zinc-200 rounded-sm shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="inline-block animate-spin h-8 w-8 border-4 border-zinc-300 border-t-orange-500 rounded-full mb-3" />
            <p className="text-zinc-500 font-bold">Loading Reports...</p>
          </CardContent>
        </Card>
      )}

      {!fetchError && !fetchingData && (
        <>
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300">
              <CardTitle className="text-xl font-bold text-zinc-950">Filter Options</CardTitle>
            </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Status Filter</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="report-status-filter" className="w-full h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Job Orders</SelectItem>
                <SelectItem value="pending_procurement">Pending Procurement</SelectItem>
                <SelectItem value="procurement_approved">Procurement Approved</SelectItem>
                <SelectItem value="assigned_to_production">Assigned to Production</SelectItem>
                <SelectItem value="under_production">Under Production</SelectItem>
                <SelectItem value="production_completed">Production Completed</SelectItem>
                <SelectItem value="scheduled_for_dispatch">Scheduled for Dispatch</SelectItem>
                <SelectItem value="ready_to_dispatch">Ready to Dispatch</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <FilterActionButtons onSearch={handleSearch} onReset={handleReset} loading={fetchingData} hasActiveFilters={hasActiveFilters} />
        </CardContent>
      </Card>

      <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Records Found</p>
              <p className="text-3xl font-light text-zinc-950">{fetchingData ? '...' : reportData.length}</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
              <p data-testid="report-total-windows" className="text-3xl font-light text-zinc-950">{totalWindows}</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
              <p data-testid="report-total-sqft" className="text-3xl font-light text-zinc-950">{totalSqft.toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardHeader className="border-b border-zinc-300">
            <CardTitle className="text-xl font-bold text-zinc-950">Export to Excel/CSV</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-zinc-500 mb-6">
              Export {filterStatus === 'all' ? 'all' : 'filtered'} job orders ({reportData.length} records) with complete details.
            </p>
            <Button
              data-testid="export-excel-button"
              onClick={exportToExcel}
              disabled={loading || reportData.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 w-full"
            >
              <FileXls size={20} weight="bold" className="mr-2" />
              {loading ? 'Exporting...' : `Export to CSV (${reportData.length} records)`}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardHeader className="border-b border-zinc-300">
            <CardTitle className="text-xl font-bold text-zinc-950">Report Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Selected Filter:</span>
                <span className="text-zinc-950 font-bold capitalize">{filterStatus.replace(/_/g, ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Export Format:</span>
                <span className="text-zinc-950 font-bold">CSV (Excel Compatible)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Includes:</span>
                <span className="text-zinc-950 font-bold">All Fields + Priority + Status + Totals</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

          {/* Empty state */}
          {reportData.length === 0 && (
            <Card className="border-zinc-200 rounded-sm shadow-sm">
              <CardContent className="p-8 text-center">
                <p className="text-zinc-500 font-bold">No Reports Found</p>
                <p className="text-sm text-zinc-400 mt-1">Try changing the status filter or check if data exists.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/* ========== Productivity Monitoring Report Tab ========== */
function ProductivityReportTab() {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [prodUser, setProdUser] = useState('all');
  const [joNumber, setJoNumber] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reportData, setReportData] = useState(null);
  const [prodUsers, setProdUsers] = useState([]);

  useEffect(() => {
    api.get(`/api/production/users`)
      .then(res => setProdUsers(res.data))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async (overrides) => {
    setFetchingData(true);
    try {
      const params = new URLSearchParams();
      const df = overrides?.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
      const dt = overrides?.dateTo !== undefined ? overrides.dateTo : dateTo;
      const pu = overrides?.prodUser !== undefined ? overrides.prodUser : prodUser;
      const jn = overrides?.joNumber !== undefined ? overrides.joNumber : joNumber;
      const sf = overrides?.statusFilter !== undefined ? overrides.statusFilter : statusFilter;
      if (df) params.append('date_from', df);
      if (dt) params.append('date_to', dt);
      if (pu !== 'all') params.append('production_user', pu);
      if (jn.trim()) params.append('jo_number', jn.trim());
      if (sf !== 'all') params.append('status', sf);
      const { data } = await api.get(`/api/production/productivity-report?${params}`);
      setReportData(data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load productivity report');
    } finally {
      setFetchingData(false);
    }
  }, [dateFrom, dateTo, prodUser, joNumber, statusFilter]);

  useEffect(() => { fetchReport(); }, []);

  const handleSearch = () => { fetchReport(); };
  const handleReset = () => {
    setDateFrom(''); setDateTo(''); setProdUser('all'); setJoNumber(''); setStatusFilter('all');
    fetchReport({ dateFrom: '', dateTo: '', prodUser: 'all', joNumber: '', statusFilter: 'all' });
  };
  const hasActiveFilters = dateFrom || dateTo || prodUser !== 'all' || joNumber || statusFilter !== 'all';

  const exportProductivityCSV = () => {
    if (!reportData?.jobs?.length) return;
    setLoading(true);
    try {
      const headers = [
        'JO Number', 'Customer Name', 'Work Location', 'Delivery Date',
        'No of Windows', 'Total Sqft', 'Production Start', 'Production End',
        'Total Time', 'Production User', 'Status'
      ];
      const rows = reportData.jobs.map(jo => [
        jo.jo_number, jo.customer_name, jo.work_location, jo.delivery_date,
        jo.no_of_windows, jo.total_sqft,
        jo.production_start_at || '', jo.production_end_at || '',
        jo.production_duration_minutes ? `${Math.floor(jo.production_duration_minutes/60)}h ${Math.round(jo.production_duration_minutes%60)}m` : '',
        jo.production_started_by || jo.production_completed_by || '',
        jo.status
      ]);
      let csv = headers.join(',') + '\n';
      rows.forEach(row => { csv += row.map(cell => `"${cell}"`).join(',') + '\n'; });
      csv += `\n"TOTALS","","","","${reportData.totals.total_windows}","${reportData.totals.total_sqft}","","","${reportData.totals.total_hours.toFixed(1)}h","","${reportData.totals.total_jobs} jobs"`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `productivity_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success(`Exported ${reportData.jobs.length} records`);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to export');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Day-wise output chart */}
      <DaywiseProductivity productionUser={prodUser} />

      {/* Filters */}
      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardHeader className="border-b border-zinc-300">
          <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
            <FunnelSimple size={20} weight="bold" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Date From</Label>
              <Input data-testid="prod-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Date To</Label>
              <Input data-testid="prod-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Production User</Label>
              <Select value={prodUser} onValueChange={setProdUser}>
                <SelectTrigger data-testid="prod-user-filter" className="w-full h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {prodUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.user_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">JO Number</Label>
              <Input data-testid="prod-jo-filter" placeholder="Search JO..." value={joNumber} onChange={e => setJoNumber(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="prod-status-filter" className="w-full h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="under_production">Under Production</SelectItem>
                  <SelectItem value="production_completed">Production Completed</SelectItem>
                  <SelectItem value="scheduled_for_dispatch">Scheduled for Dispatch</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FilterActionButtons onSearch={handleSearch} onReset={handleReset} loading={fetchingData} hasActiveFilters={hasActiveFilters} />
          </div>
        </CardContent>
      </Card>

      {/* Totals Summary */}
      {reportData && (
        <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Jobs</p>
                <p data-testid="prod-report-total-jobs" className="text-3xl font-light text-zinc-950">{fetchingData ? '...' : reportData.totals.total_jobs}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                <p data-testid="prod-report-total-windows" className="text-3xl font-light text-zinc-950">{reportData.totals.total_windows}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
                <p data-testid="prod-report-total-sqft" className="text-3xl font-light text-zinc-950">{reportData.totals.total_sqft}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Prod Hours</p>
                <p data-testid="prod-report-total-hours" className="text-3xl font-light text-purple-700">{reportData.totals.total_hours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      <div className="flex justify-end">
        <Button
          data-testid="export-productivity-csv"
          onClick={exportProductivityCSV}
          disabled={loading || !reportData?.jobs?.length}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11"
        >
          <FileXls size={20} weight="bold" className="mr-2" />
          {loading ? 'Exporting...' : `Export to CSV${reportData?.jobs?.length ? ` (${reportData.jobs.length})` : ''}`}
        </Button>
      </div>

      {/* Data Table */}
      {(() => {
        if (reportData && reportData.jobs.length > 0) {
          return (
            <Card data-testid="productivity-report-table" className="border-zinc-300 rounded-sm shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">JO No</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Customer</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Work Location</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Delivery Date</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Windows</th>
                    <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Sqft</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Start Time</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">End Time</th>
                    <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Total Time</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Prod User</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.jobs.map(jo => (
                    <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3 font-bold text-zinc-950 whitespace-nowrap">{jo.jo_number}</td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.customer_name}</td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.work_location}</td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.delivery_date}</td>
                      <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                      <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{formatDateTime(jo.production_start_at)}</td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{formatDateTime(jo.production_end_at)}</td>
                      <td className="p-3 text-center text-purple-700 font-bold whitespace-nowrap">{formatDuration(jo.production_duration_minutes)}</td>
                      <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.production_started_by || jo.production_completed_by || '--'}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className="text-xs font-bold capitalize px-2 py-1 rounded bg-zinc-100 text-zinc-700">
                          {(jo.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
          );
        }
        if (reportData && reportData.jobs.length === 0) {
          return <EmptySearchResult onReset={handleReset} />;
        }
        return null;
      })()}
    </div>
  );
}

/* ========== Dispatch Report Tab ========== */
function DispatchReportTab() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [driverName, setDriverName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [executiveName, setExecutiveName] = useState('all');
  const [joNumber, setJoNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [execNames, setExecNames] = useState([]);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    api.get('/api/reports/executive-names')
      .then(res => setExecNames(res.data || []))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async (overrides) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const s = overrides?.status !== undefined ? overrides.status : status;
      const dn = overrides?.driverName !== undefined ? overrides.driverName : driverName;
      const vn = overrides?.vehicleNumber !== undefined ? overrides.vehicleNumber : vehicleNumber;
      const en = overrides?.executiveName !== undefined ? overrides.executiveName : executiveName;
      const jn = overrides?.joNumber !== undefined ? overrides.joNumber : joNumber;
      const cn = overrides?.customerName !== undefined ? overrides.customerName : customerName;
      if (s !== 'all') params.append('status', s);
      if (dn.trim()) params.append('driver_name', dn.trim());
      if (vn.trim()) params.append('vehicle_number', vn.trim());
      if (en && en !== 'all') params.append('executive_name', en);
      if (jn.trim()) params.append('jo_number', jn.trim());
      if (cn.trim()) params.append('customer_name', cn.trim());
      const { data } = await api.get(`/api/installation/dispatch-report?${params}`);
      setReportData(data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load dispatch report');
    } finally {
      setLoading(false);
    }
  }, [status, driverName, vehicleNumber, executiveName, joNumber, customerName]);

  useEffect(() => { fetchReport(); }, []);

  const handleSearch = () => { fetchReport(); };
  const handleReset = () => {
    setStatus('all'); setDriverName(''); setVehicleNumber('');
    setExecutiveName('all'); setJoNumber(''); setCustomerName('');
    fetchReport({ status: 'all', driverName: '', vehicleNumber: '', executiveName: 'all', joNumber: '', customerName: '' });
  };
  const hasActiveFilters = status !== 'all' || driverName || vehicleNumber || (executiveName && executiveName !== 'all') || joNumber || customerName;

  const exportCSV = () => {
    if (!reportData?.jobs?.length) return;
    const headers = ['JO Number','Customer','Executive','Location','Delivery Date','Total Windows','Dispatched','Pending','Sqft','Status','Scheduled Date','Dispatch Date','Vehicle','Driver','Installation Status','Updated'];
    const rows = reportData.jobs.map(jo => [
      jo.jo_number, jo.customer_name, jo.executive_name, jo.work_location,
      jo.delivery_date, jo.no_of_windows || 0, jo.windows_dispatched || 0,
      jo.windows_pending ?? ((jo.no_of_windows || 0) - (jo.windows_dispatched || 0)),
      jo.total_sqft, (jo.status||'').replace(/_/g,' '),
      jo.dispatch_schedule_date || '', jo.last_dispatch_date||'',
      jo.last_vehicle_number||'', jo.last_driver_name||'',
      getInstallStatusExportLabel(jo.status),
      jo.updated_at || ''
    ]);
    let csv = headers.join(',') + '\n';
    rows.forEach(r => { csv += r.map(c => `"${c}"`).join(',') + '\n'; });
    csv += `\n"TOTALS","","","","","${reportData.totals.total_windows}","${reportData.totals.total_dispatched}","${reportData.totals.total_pending}","${reportData.totals.total_sqft}","","","","","","",""`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `dispatch_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success(`Exported ${reportData.jobs.length} records`);
  };

  return (
    <div className="space-y-6">
      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="dispatch-report-status" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="production_completed">Production Completed</SelectItem>
                  <SelectItem value="scheduled_for_dispatch">Scheduled</SelectItem>
                  <SelectItem value="partially_dispatched">Partially Dispatched</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="pending_for_installation">Pending Installation</SelectItem>
                  <SelectItem value="installation_completed">Installation Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Executive</Label>
              <Select value={executiveName} onValueChange={setExecutiveName}>
                <SelectTrigger data-testid="dispatch-report-exec" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Executives</SelectItem>
                  {execNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">JO Number</Label>
              <Input data-testid="dispatch-report-jo" value={joNumber} onChange={e => setJoNumber(e.target.value)} placeholder="Search JO..." className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Customer Name</Label>
              <Input data-testid="dispatch-report-customer" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Search customer..." className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Driver</Label>
              <Input data-testid="dispatch-report-driver" value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="Search driver..." className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Vehicle</Label>
              <Input data-testid="dispatch-report-vehicle" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} placeholder="Search vehicle..." className="h-11" />
            </div>
          </div>
          <FilterActionButtons onSearch={handleSearch} onReset={handleReset} loading={loading} hasActiveFilters={hasActiveFilters} />
        </CardContent>
      </Card>

      {reportData && (
        <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-around flex-wrap gap-6">
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Jobs</p>
                <p className="text-3xl font-light text-zinc-950">{loading ? '...' : reportData.totals.total_jobs}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                <p className="text-3xl font-light text-zinc-950">{reportData.totals.total_windows}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Dispatched</p>
                <p className="text-3xl font-light text-orange-600">{reportData.totals.total_dispatched}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600">Pending</p>
                <p className="text-3xl font-light text-red-600">{reportData.totals.total_pending}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Sqft</p>
                <p className="text-3xl font-light text-zinc-950">{reportData.totals.total_sqft}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button data-testid="export-dispatch-report" onClick={exportCSV} disabled={!reportData?.jobs?.length} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11">
          <FileXls size={20} weight="bold" className="mr-2" />
          Export CSV{reportData?.jobs?.length ? ` (${reportData.jobs.length})` : ''}
        </Button>
      </div>

      {reportData && reportData.jobs.length > 0 && (
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">JO No</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Customer</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Executive</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Location</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Delivery</th>
                    <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Total Win.</th>
                    <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-orange-500">Dispatched</th>
                    <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-red-500">Pending</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Status</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Sched. Date</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Vehicle</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Driver</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Install</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.jobs.map(jo => {
                    const pending = jo.windows_pending ?? ((jo.no_of_windows || 0) - (jo.windows_dispatched || 0));
                    const installStatus = getInstallStatusLabel(jo.status);
                    const installClass = getInstallStatusBadgeClass(installStatus);
                    return (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-3 font-bold text-zinc-950 whitespace-nowrap">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.executive_name}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.work_location}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.delivery_date}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.no_of_windows || 0}</td>
                        <td className="p-3 text-center text-orange-600 font-bold">{jo.windows_dispatched || 0}</td>
                        <td className="p-3 text-center text-red-600 font-bold">{pending}</td>
                        <td className="p-3 whitespace-nowrap"><span className="text-xs font-bold capitalize px-2 py-1 rounded bg-zinc-100 text-zinc-700">{(jo.status||'').replace(/_/g,' ')}</span></td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.dispatch_schedule_date || '--'}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.last_vehicle_number || '--'}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.last_driver_name || '--'}</td>
                        <td className="p-3 whitespace-nowrap">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${installClass}`}>{installStatus}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ========== Session Logs Tab ========== */
function SessionLogsTab() {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchLogs = useCallback(async (overrides) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const df = overrides?.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
      const dt = overrides?.dateTo !== undefined ? overrides.dateTo : dateTo;
      const rf = overrides?.roleFilter !== undefined ? overrides.roleFilter : roleFilter;
      if (df) params.append('date_from', df);
      if (dt) params.append('date_to', dt);
      if (rf !== 'all') params.append('role', rf);
      const { data } = await api.get(`/api/session-logs?${params}`);
      setLogs(data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load session logs');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, roleFilter]);

  useEffect(() => { fetchLogs(); }, []);

  const handleSearch = () => { fetchLogs(); };
  const handleReset = () => {
    setDateFrom(''); setDateTo(''); setRoleFilter('all');
    fetchLogs({ dateFrom: '', dateTo: '', roleFilter: 'all' });
  };
  const hasActiveFilters = dateFrom || dateTo || roleFilter !== 'all';

  return (
    <div className="space-y-6">
      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Date From</Label>
              <Input data-testid="session-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Date To</Label>
              <Input data-testid="session-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="session-role-filter" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="executive">Executive</SelectItem>
                  <SelectItem value="procurement">Procurement</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <FilterActionButtons onSearch={handleSearch} onReset={handleReset} loading={loading} hasActiveFilters={hasActiveFilters} />
        </CardContent>
      </Card>

      <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sessions</p>
              <p className="text-3xl font-light text-zinc-950">{loading ? '...' : logs.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {logs.length > 0 && (
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">User</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Role</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Login Time</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Logout Time</th>
                    <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={log.session_id || idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3 font-bold text-zinc-950">{log.user_name || log.user_id}</td>
                      <td className="p-3"><span className="text-xs font-bold capitalize px-2 py-1 rounded bg-zinc-100 text-zinc-700">{log.role}</span></td>
                      <td className="p-3 text-zinc-700">{formatDateTime(log.login_at)}</td>
                      <td className="p-3 text-zinc-700">{log.logout_at ? formatDateTime(log.logout_at) : <span className="text-emerald-600 font-bold text-xs">Active</span>}</td>
                      <td className="p-3 text-zinc-700">{log.session_date || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {!loading && logs.length === 0 && (
        <EmptySearchResult onReset={handleReset} />
      )}
    </div>
  );
}

/* ========== Executive-wise Report Tab ========== */
function ExecutiveWiseTab() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [execFilter, setExecFilter] = useState('all');
  const [execNames, setExecNames] = useState([]);
  const [expandedExec, setExpandedExec] = useState(null);

  useEffect(() => {
    api.get(`/api/reports/executive-names`)
      .then(res => setExecNames(res.data))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async (overrides) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const df = overrides?.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
      const dt = overrides?.dateTo !== undefined ? overrides.dateTo : dateTo;
      const ef = overrides?.execFilter !== undefined ? overrides.execFilter : execFilter;
      if (df) params.append('date_from', df);
      if (dt) params.append('date_to', dt);
      if (ef && ef !== 'all') params.append('executive_name', ef);
      const res = await api.get(`/api/reports/executive-wise?${params}`);
      setData(res.data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load executive report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, execFilter]);

  useEffect(() => { fetchReport(); }, []);

  const handleSearch = () => { fetchReport(); };
  const handleReset = () => {
    setDateFrom(''); setDateTo(''); setExecFilter('all');
    fetchReport({ dateFrom: '', dateTo: '', execFilter: 'all' });
  };
  const hasActiveFilters = dateFrom || dateTo || (execFilter && execFilter !== 'all');

  const exportCSV = () => {
    if (!data?.executives?.length) return;
    const rows = [];
    for (const exec of data.executives) {
      for (const jo of exec.jobs) {
        rows.push([exec.executive_name, jo.jo_number, jo.customer_name, jo.customer_mobile, jo.work_location, jo.delivery_date, jo.no_of_windows, jo.total_sqft, jo.status].join(','));
      }
    }
    const header = 'Executive,JO No,Customer,Mobile,Location,Delivery,Windows,Sqft,Status';
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `executive_wise_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Executive Name</Label>
          <Select value={execFilter} onValueChange={setExecFilter}>
            <SelectTrigger data-testid="exec-name-filter" className="w-56 h-10">
              <SelectValue placeholder="All Executives" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Executives</SelectItem>
              {execNames.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">From Date</Label>
          <Input data-testid="exec-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">To Date</Label>
          <Input data-testid="exec-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-44" />
        </div>
        <Button data-testid="exec-export-csv" onClick={exportCSV} disabled={!data?.executives?.length} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10">
          <FileXls size={16} weight="bold" className="mr-2" /> Export CSV
        </Button>
      </div>
      <FilterActionButtons onSearch={handleSearch} onReset={handleReset} loading={loading} hasActiveFilters={hasActiveFilters} />

      {loading && <div className="text-center py-8 text-zinc-500">Loading...</div>}

      {!loading && data && (
        <>
          {/* Grand Totals */}
          <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-around flex-wrap gap-4">
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Executives</p>
                  <p data-testid="exec-total-count" className="text-2xl font-light text-zinc-950">{data.executives.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Jobs</p>
                  <p data-testid="exec-total-jobs" className="text-2xl font-light text-zinc-950">{data.grand_totals.total_jobs}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                  <p className="text-2xl font-light text-zinc-950">{data.grand_totals.total_windows}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
                  <p className="text-2xl font-light text-zinc-950">{data.grand_totals.total_sqft}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Executive Summary Table */}
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Executive</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Total Jobs</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Pending</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">In Prod</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Completed</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.executives.map(exec => (
                      <tr key={exec.executive_name} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-3 font-bold text-zinc-950">{exec.executive_name}</td>
                        <td className="p-3 text-center text-zinc-700">{exec.total_jobs}</td>
                        <td className="p-3 text-center text-zinc-700">{exec.total_windows}</td>
                        <td className="p-3 text-center text-zinc-700">{exec.total_sqft}</td>
                        <td className="p-3 text-center"><span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-bold">{exec.pending}</span></td>
                        <td className="p-3 text-center"><span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-bold">{exec.in_production}</span></td>
                        <td className="p-3 text-center"><span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">{exec.completed}</span></td>
                        <td className="p-3 text-center">
                          <button
                            data-testid={`expand-exec-${exec.executive_name}`}
                            onClick={() => setExpandedExec(expandedExec === exec.executive_name ? null : exec.executive_name)}
                            className="text-orange-600 hover:text-orange-700 text-xs font-bold underline"
                          >
                            {expandedExec === exec.executive_name ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Expanded JO detail for selected executive */}
          {expandedExec && data.executives.find(e => e.executive_name === expandedExec) && (
            <Card className="border-orange-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-orange-200 bg-orange-50 py-3">
                <CardTitle className="text-lg font-bold text-zinc-950">
                  Job Orders by {expandedExec}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Mobile</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.executives.find(e => e.executive_name === expandedExec).jobs.map(jo => (
                        <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="p-3 font-bold text-zinc-950">{jo.jo_number}</td>
                          <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                          <td className="p-3 text-zinc-700">{jo.customer_mobile}</td>
                          <td className="p-3 text-zinc-700">{jo.work_location}</td>
                          <td className="p-3 text-zinc-700">{jo.delivery_date}</td>
                          <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                          <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                          <td className="p-3 text-center text-xs font-bold text-zinc-600">{jo.status?.replace(/_/g, ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/* ========== Executive Monthly Sales Tab ========== */
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatMonthLabel(mk) {
  const [y, m] = mk.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function ExecutiveMonthlySalesTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState('all');
  const [month, setMonth] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (y, m) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      if (y && y !== 'all') params.append('year', y);
      if (m && m !== 'all') params.append('month', m);
      const { data } = await api.get(`/api/admin/reports/executive-monthly-sales?${params}`);
      setData(data);
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load Executive Monthly Sales report');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(year, month); }, [load, year, month]);

  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="space-y-6" data-testid="executive-monthly-sales-tab">
      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardContent className="p-4 flex items-end gap-4 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="ems-year-filter" className="w-36 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="ems-month-filter" className="w-40 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {MONTH_NAMES.map((name, i) => <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="ems-refresh-btn" variant="outline" onClick={() => load(year, month)} disabled={loading} className="h-10">
            <ArrowsClockwise size={16} weight="bold" className="mr-1.5" /> Refresh
          </Button>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-zinc-400">Loading report…</div>}
      {!loading && loadError && <LoadErrorState onRetry={() => load(year, month)} />}
      {!loading && !loadError && data && data.rows.length === 0 && (
        <div className="text-center py-12 text-zinc-400" data-testid="ems-empty">No data for the selected period.</div>
      )}
      {!loading && !loadError && data && data.rows.length > 0 && (
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="ems-table">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-3 text-left">Executive Name</th>
                  <th className="p-3 text-left">Month</th>
                  <th className="p-3 text-right">Total Quotations</th>
                  <th className="p-3 text-right">Total Converted</th>
                  <th className="p-3 text-right">Conversion Rate (%)</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={`${r.executive_name}-${r.month}`} className="border-b border-zinc-100" data-testid={`ems-row-${i}`}>
                    <td className="p-3 font-bold text-zinc-900">{r.executive_name}</td>
                    <td className="p-3 text-zinc-700">{formatMonthLabel(r.month)}</td>
                    <td className="p-3 text-right font-mono">{r.total_quotations}</td>
                    <td className="p-3 text-right font-mono">{r.total_converted}</td>
                    <td className="p-3 text-right">
                      <span className={`font-bold ${r.conversion_rate >= 50 ? 'text-emerald-600' : r.conversion_rate >= 25 ? 'text-amber-600' : 'text-zinc-600'}`}>
                        {r.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-zinc-50 font-bold text-zinc-900">
                <tr data-testid="ems-totals-row">
                  <td className="p-3 uppercase text-xs tracking-wider" colSpan={2}>Totals</td>
                  <td className="p-3 text-right font-mono">{data.totals.total_quotations}</td>
                  <td className="p-3 text-right font-mono">{data.totals.total_converted}</td>
                  <td className="p-3 text-right">{data.totals.conversion_rate}%</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ========== Monthly Sales Report Tab ========== */
const fmtINRr = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

function MonthlySalesTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (y, m) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      if (y && y !== 'all') params.append('year', y);
      if (m && m !== 'all') params.append('month', m);
      const { data } = await api.get(`/api/admin/reports/monthly-sales?${params}`);
      setData(data);
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load Monthly Sales report');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(year, month); }, [load, year, month]);

  const downloadCsv = (headers, rows, name) => {
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const exportAll = () => {
    if (!data) return;
    const rows = [
      ['MONTHLY SUMMARY'], ['Month', 'JOs Converted', 'Sales Value', 'Total Sqft', 'Collection', 'Receipts'],
      ...data.monthly.map(r => [formatMonthLabel(r.month), r.jo_count, r.total_value, r.total_sqft, r.collection, r.receipt_count]),
      ['TOTAL', data.totals.jo_count, data.totals.total_value, data.totals.total_sqft, data.totals.collection, data.totals.receipt_count],
      [], ['EXECUTIVE-WISE SALES'], ['Month', 'Executive', 'JOs Converted', 'Sales Value', 'Total Sqft'],
      ...data.executive_sales.map(r => [formatMonthLabel(r.month), r.executive_name, r.jo_count, r.total_value, r.total_sqft]),
      [], ['EXECUTIVE-WISE COLLECTIONS'], ['Month', 'Executive', 'Receipts', 'Amount Collected'],
      ...data.executive_collections.map(r => [formatMonthLabel(r.month), r.executive_name, r.receipt_count, r.amount]),
    ];
    downloadCsv(rows[0], rows.slice(1), `monthly_sales_report_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];
  const th = 'p-3 font-bold text-[10px] uppercase tracking-wider text-zinc-500';

  return (
    <div className="space-y-6" data-testid="monthly-sales-tab">
      {/* Filters */}
      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardContent className="p-4 flex items-end gap-4 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="ms-year-filter" className="w-36 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="ms-month-filter" className="w-40 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {MONTH_NAMES.map((name, i) => <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="ms-refresh-btn" variant="outline" onClick={() => load(year, month)} disabled={loading} className="h-10">
            <ArrowsClockwise size={16} weight="bold" className="mr-1.5" /> Refresh
          </Button>
          <Button data-testid="ms-export-btn" onClick={exportAll} disabled={!data} className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
            <FileXls size={16} weight="bold" className="mr-1.5" /> Export CSV
          </Button>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-zinc-400">Loading report…</div>}
      {!loading && loadError && <LoadErrorState onRetry={() => load(year, month)} />}
      {!loading && !loadError && data && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[['JOs Converted', data.totals.jo_count], ['Sales Value', fmtINRr(data.totals.total_value)],
              ['Total Sqft', data.totals.total_sqft], ['Collection', fmtINRr(data.totals.collection)],
              ['Receipts', data.totals.receipt_count]].map(([k, v]) => (
              <Card key={k} className="border-zinc-300 rounded-sm shadow-sm">
                <CardContent className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{k}</p>
                  <p className="text-xl font-bold text-zinc-950" data-testid={`ms-total-${k.split(' ')[0].toLowerCase()}`}>{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* 1. Monthly summary */}
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300 py-3 px-6">
              <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Monthly Summary — Quotations Converted to JOs & Collections</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ms-monthly-table">
                <thead className="bg-zinc-100"><tr>
                  <th className={`${th} text-left`}>Month</th>
                  <th className={`${th} text-right`}>JOs Converted</th>
                  <th className={`${th} text-right`}>Sales Value</th>
                  <th className={`${th} text-right`}>Total Sqft</th>
                  <th className={`${th} text-right`}>Collection</th>
                  <th className={`${th} text-right`}>Receipts</th>
                </tr></thead>
                <tbody>
                  {data.monthly.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-400">No data for the selected period</td></tr>}
                  {data.monthly.map(r => (
                    <tr key={r.month} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ms-month-row-${r.month}`}>
                      <td className="p-3 font-bold text-zinc-900">{formatMonthLabel(r.month)}</td>
                      <td className="p-3 text-right font-mono">{r.jo_count}</td>
                      <td className="p-3 text-right font-bold text-zinc-900">{fmtINRr(r.total_value)}</td>
                      <td className="p-3 text-right font-mono">{r.total_sqft}</td>
                      <td className="p-3 text-right font-bold text-emerald-700">{fmtINRr(r.collection)}</td>
                      <td className="p-3 text-right font-mono">{r.receipt_count}</td>
                    </tr>
                  ))}
                </tbody>
                {data.monthly.length > 0 && (
                  <tfoot className="bg-zinc-50 font-bold text-zinc-900"><tr data-testid="ms-monthly-totals">
                    <td className="p-3 uppercase text-xs tracking-wider">Total</td>
                    <td className="p-3 text-right font-mono">{data.totals.jo_count}</td>
                    <td className="p-3 text-right">{fmtINRr(data.totals.total_value)}</td>
                    <td className="p-3 text-right font-mono">{data.totals.total_sqft}</td>
                    <td className="p-3 text-right text-emerald-700">{fmtINRr(data.totals.collection)}</td>
                    <td className="p-3 text-right font-mono">{data.totals.receipt_count}</td>
                  </tr></tfoot>
                )}
              </table>
            </CardContent>
          </Card>

          {/* 2. Executive-wise sales */}
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300 py-3 px-6">
              <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Executive-wise Sales (Quotations Converted to JOs)</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ms-exec-sales-table">
                <thead className="bg-zinc-100"><tr>
                  <th className={`${th} text-left`}>Month</th>
                  <th className={`${th} text-left`}>Executive</th>
                  <th className={`${th} text-right`}>JOs Converted</th>
                  <th className={`${th} text-right`}>Sales Value</th>
                  <th className={`${th} text-right`}>Total Sqft</th>
                </tr></thead>
                <tbody>
                  {data.executive_sales.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-400">No data for the selected period</td></tr>}
                  {data.executive_sales.map((r, i) => (
                    <tr key={`${r.month}-${r.executive_name}`} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ms-exec-sales-row-${i}`}>
                      <td className="p-3 text-zinc-700">{formatMonthLabel(r.month)}</td>
                      <td className="p-3 font-bold text-zinc-900">{r.executive_name}</td>
                      <td className="p-3 text-right font-mono">{r.jo_count}</td>
                      <td className="p-3 text-right font-bold">{fmtINRr(r.total_value)}</td>
                      <td className="p-3 text-right font-mono">{r.total_sqft}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* 3. Executive-wise collections */}
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300 py-3 px-6">
              <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Executive-wise Collection Report</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ms-exec-coll-table">
                <thead className="bg-zinc-100"><tr>
                  <th className={`${th} text-left`}>Month</th>
                  <th className={`${th} text-left`}>Executive</th>
                  <th className={`${th} text-right`}>Receipts</th>
                  <th className={`${th} text-right`}>Amount Collected</th>
                </tr></thead>
                <tbody>
                  {data.executive_collections.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-400">No collections for the selected period</td></tr>}
                  {data.executive_collections.map((r, i) => (
                    <tr key={`${r.month}-${r.executive_name}`} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ms-exec-coll-row-${i}`}>
                      <td className="p-3 text-zinc-700">{formatMonthLabel(r.month)}</td>
                      <td className="p-3 font-bold text-zinc-900">{r.executive_name}</td>
                      <td className="p-3 text-right font-mono">{r.receipt_count}</td>
                      <td className="p-3 text-right font-bold text-emerald-700">{fmtINRr(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/* ========== Main Reports Page with Tabs ========== */
export function Reports() {
  const [activeTab, setActiveTab] = useState('jo-export');

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Reports & Export</h1>
          <p className="text-sm text-zinc-500 mt-2">Export job order data and monitor production productivity</p>
        </div>

        {/* Tab Navigation */}
        <div data-testid="report-tabs" className="flex gap-2 border-b border-zinc-200 pb-0">
          <button
            data-testid="tab-jo-export"
            onClick={() => setActiveTab('jo-export')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'jo-export'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <FileXls size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Job Orders Export
          </button>
          <button
            data-testid="tab-productivity"
            onClick={() => setActiveTab('productivity')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'productivity'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <Timer size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Productivity
          </button>
          <button
            data-testid="tab-dispatch"
            onClick={() => setActiveTab('dispatch')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'dispatch'
                ? 'border-cyan-500 text-cyan-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <Truck size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Dispatch Report
          </button>
          <button
            data-testid="tab-sessions"
            onClick={() => setActiveTab('sessions')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'sessions'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <UserList size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Session Logs
          </button>
          <button
            data-testid="tab-executive-wise"
            onClick={() => setActiveTab('executive-wise')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'executive-wise'
                ? 'border-rose-500 text-rose-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <IdentificationBadge size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Executive-wise
          </button>
          <button
            data-testid="tab-exec-monthly-sales"
            onClick={() => setActiveTab('exec-monthly-sales')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'exec-monthly-sales'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <ChartLineUp size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Executive Monthly Sales
          </button>
          <button
            data-testid="tab-monthly-sales"
            onClick={() => setActiveTab('monthly-sales')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'monthly-sales'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <ChartLineUp size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Monthly Sales
          </button>
        </div>

        {activeTab === 'jo-export' && <JOExportTab />}
        {activeTab === 'productivity' && <ProductivityReportTab />}
        {activeTab === 'dispatch' && <DispatchReportTab />}
        {activeTab === 'sessions' && <SessionLogsTab />}
        {activeTab === 'executive-wise' && <ExecutiveWiseTab />}
        {activeTab === 'exec-monthly-sales' && <ExecutiveMonthlySalesTab />}
        {activeTab === 'monthly-sales' && <MonthlySalesTab />}
      </div>
    </Layout>
  );
}
